import React, { useRef, useEffect, useState } from 'react';
import {
	motion
} from 'framer-motion';
import {
	getCellBorders
} from '../utils/gridUtils';
import { useViewportSize } from '../hooks/useViewportSize';

const ColorGrid = ({
	grid,
	gridWidth,
	gridHeight,
	ownership,
	handleTileClick,
	isCellAnimating,
	getCellAnimationStyle,
	availableColors,
	playerColor,
	aiColor,
	isPlayerTurn,
	gameOver,
	isAnimating,
	currentPlayer
}) => {
	const containerRef = useRef(null);
	const { width: viewportWidth, height: viewportHeight } = useViewportSize();
	const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

	// Update container size when viewport changes
	useEffect(() => {
		if (containerRef.current) {
			const updateSize = () => {
				const container = containerRef.current;
				if (container) {
					// Get the parent container's available space
					const parent = container.parentElement;
					if (parent) {
						const parentRect = parent.getBoundingClientRect();
						setContainerSize({
							width: parentRect.width - 40, // Account for padding
							height: viewportHeight - 300 // Account for header, controls, etc.
						});
					}
				}
			};

			updateSize();
			window.addEventListener('resize', updateSize);
			return () => window.removeEventListener('resize', updateSize);
		}
	}, [viewportWidth, viewportHeight]);

	// Calculate responsive cell size based on available space and grid dimensions
	const calculateCellSize = () => {
		// Minimum and maximum cell sizes
		const minCellSize = 20; // Minimum for touch targets on mobile
		const maxCellSize = 60; // Maximum for large screens (increased from 50)
		
		// Available space (accounting for padding and margins)
		const availableWidth = containerSize.width || viewportWidth - 80;
		const availableHeight = containerSize.height || viewportHeight - 300;
		
		// Calculate cell size based on available space
		const widthBasedSize = Math.floor((availableWidth - 20) / gridWidth); // -20 for padding
		const heightBasedSize = Math.floor((availableHeight - 20) / gridHeight);
		
		// Use the smaller dimension to ensure grid fits
		let cellSize = Math.min(widthBasedSize, heightBasedSize);
		
		// Apply min/max constraints
		cellSize = Math.max(minCellSize, Math.min(maxCellSize, cellSize));
		
		// Also consider grid dimension for very large grids
		// Adjust constraints to allow larger cells for 25x15 grid
		const maxDimension = Math.max(gridWidth, gridHeight);
		if (maxDimension > 25) {
			// Very large grids: cap at 30px to ensure visibility
			cellSize = Math.min(cellSize, 30);
		} else if (maxDimension > 20) {
			// Large grids (21-25): allow up to 60px to maintain similar cell size
			cellSize = Math.min(cellSize, 60);
		} else if (maxDimension > 15) {
			// Medium-large grids (16-20): allow up to 60px (same as maxCellSize)
			cellSize = Math.min(cellSize, 60);
		}
		// For grids <= 15, no additional constraint (uses maxCellSize)
		
		return cellSize;
	};

	const cellSize = calculateCellSize();
	
	return (
		<div ref={containerRef} style={{
			display: "grid",
			gridTemplateColumns: `repeat(${gridWidth}, ${cellSize}px)`,
			gridTemplateRows: `repeat(${gridHeight}, ${cellSize}px)`,
			gap: "0",
			margin: "auto",
			width: "fit-content",
			boxShadow: "0 0 10px rgba(0,0,0,0.2)",
			padding: "10px",
			borderRadius: "5px",
			background: "#333333",
			maxWidth: "100%",
			overflow: "hidden"
		}}>
			{grid.map((row, rowIndex) =>
				row.map((color, colIndex) => {
					const borderStyles = ownership[rowIndex][colIndex]
						? getCellBorders(grid, ownership, rowIndex, colIndex, gridWidth, gridHeight)
						: {};
					
					// Determine if this color is available for selection
					const isAvailable = isPlayerTurn &&
						!gameOver &&
						!isAnimating &&
						availableColors.includes(color) &&
						color !== aiColor;
					
					// Determine if this cell is currently being animated
					const cellIsAnimating = isCellAnimating(rowIndex, colIndex);
					const cellAnimationStyle = getCellAnimationStyle(rowIndex, colIndex);
					const highlightStyle = cellIsAnimating ? {
						boxShadow: `0 0 10px 3px ${currentPlayer === "Player" ? "white" : "black"}`,
						zIndex: 5
					} : {};
					
					// Marker size adjustment based on cell size
					const markerSize = Math.max(cellSize * 0.25, 8);
					const markerOffset = Math.max(cellSize * 0.125, 5);
					
					return (
						<motion.div key={`${rowIndex}-${colIndex}`} animate={cellIsAnimating ? cellAnimationStyle : {}} style={{
							width: `${cellSize}px`,
							height: `${cellSize}px`,
							backgroundColor: color,
							position: "relative",
							...borderStyles,
							...highlightStyle,
							boxSizing: "border-box",
							cursor: isAvailable ? "pointer" : "default"
						}} onClick={() => handleTileClick(color)} whileHover={isAvailable ? {scale: 1.05} : {}}>
							{rowIndex === 0 && colIndex === 0 && (
								<div style={{
									position: "absolute",
									top: `${markerOffset}px`,
									left: `${markerOffset}px`,
									width: `${markerSize}px`,
									height: `${markerSize}px`,
									borderRadius: "50%",
									backgroundColor: "white",
									zIndex: 10,
									boxShadow: "0 0 2px #000"
								}}></div>
							)} {rowIndex === gridHeight - 1 && colIndex === gridWidth - 1 && (
							<div style={{
								position: "absolute",
								bottom: `${markerOffset}px`,
								right: `${markerOffset}px`,
								width: `${markerSize}px`,
								height: `${markerSize}px`,
								borderRadius: "50%",
								backgroundColor: "black",
								zIndex: 10,
								boxShadow: "0 0 2px #fff"
							}}></div>
						)}
						</motion.div>
					);
				})
			)}
		</div>
	);
};

export default ColorGrid;
